import { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, ArrowLeft, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "ELECTROTECHNIQUE", label: "Électrotechnique", color: "bg-[#0F76B8]" },
  { value: "IRVE", label: "IRVE", color: "bg-[#3DAE2B]" },
  { value: "LOCATION", label: "Location", color: "bg-[#FBBF24] text-[#0F172A]" },
  { value: "DAO", label: "DAO", color: "bg-[#1B3A6B]" },
];

const EMPTY = { category: "IRVE", title: "", description: "", image_url: "", sort_order: 0, is_published: true };

export default function AdminRealisations() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/realisations");
      setItems(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("ingelec_admin_token");
        nav("/admin/login");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 8 Mo)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...it }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.image_url) {
      toast.error("Image obligatoire");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Titre obligatoire");
      return;
    }
    setSaving(true);
    const payload = {
      category: form.category,
      title: form.title.trim(),
      description: form.description?.trim() || null,
      image_url: form.image_url,
      sort_order: Number(form.sort_order) || 0,
      is_published: !!form.is_published,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/realisations", payload);
        toast.success("Chantier ajouté");
      } else {
        await api.put(`/admin/realisations/${editing}`, payload);
        toast.success("Chantier mis à jour");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!del) return;
    try {
      await api.delete(`/admin/realisations/${del.id}`);
      toast.success("Chantier supprimé");
      setDel(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  const filtered = filter === "ALL" ? items : items.filter((i) => i.category === filter);
  const catMeta = (v) => CATEGORIES.find((c) => c.value === v) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="page-admin-realisations">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-500 hover:text-[#1B3A6B] flex items-center gap-1.5 text-sm" data-testid="admin-back-catalog">
              <ArrowLeft className="w-4 h-4" /> Catalogue
            </Link>
            <div className="h-5 w-px bg-slate-200" />
            <span className="font-display font-bold text-[#1B3A6B]">Admin · Chantiers récents</span>
          </div>
          <Button onClick={openCreate} className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold rounded-full" data-testid="admin-add-realisation">
            <Plus className="w-4 h-4 mr-1.5" /> Ajouter un chantier
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="realisations-filters">
          <button
            onClick={() => setFilter("ALL")}
            className={`px-4 h-9 rounded-full text-sm font-medium transition ${
              filter === "ALL" ? "bg-[#1B3A6B] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
            data-testid="filter-ALL"
          >
            Tous ({items.length})
          </button>
          {CATEGORIES.map((c) => {
            const n = items.filter((i) => i.category === c.value).length;
            return (
              <button
                key={c.value}
                onClick={() => setFilter(c.value)}
                className={`px-4 h-9 rounded-full text-sm font-medium transition ${
                  filter === c.value ? "bg-[#1B3A6B] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
                data-testid={`filter-${c.value}`}
              >
                {c.label} ({n})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-2xl">
            Aucun chantier dans cette catégorie. Cliquez sur "Ajouter un chantier".
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((it) => {
              const cat = catMeta(it.category);
              return (
                <div key={it.id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`realisation-card-${it.id}`}>
                  <div className="relative h-44 bg-slate-100">
                    <img src={it.image_url} alt={it.title} className="w-full h-full object-cover" />
                    <div className={`absolute top-2 left-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold text-white ${cat.color}`}>
                      {cat.label}
                    </div>
                    {!it.is_published && (
                      <div className="absolute top-2 right-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-900/80 text-white">
                        Brouillon
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-slate-900 line-clamp-1">{it.title}</div>
                    {it.description && (
                      <p className="mt-1 text-xs text-slate-600 line-clamp-2">{it.description}</p>
                    )}
                    <div className="mt-3 flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`realisation-edit-${it.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDel(it)} data-testid={`realisation-delete-${it.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Ajouter un chantier" : "Modifier le chantier"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Catégorie *</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, category: c.value }))}
                    className={`px-3 h-8 rounded-full text-xs font-semibold transition ${
                      form.category === c.value ? `${c.color} text-white` : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                    data-testid={`form-category-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="r-title">Titre *</Label>
              <Input id="r-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Installation borne 22kW Saint-Denis" data-testid="form-title" />
            </div>
            <div>
              <Label htmlFor="r-desc">Description (optionnel)</Label>
              <Textarea id="r-desc" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Quelques mots sur le chantier…" data-testid="form-description" />
            </div>
            <div>
              <Label>Image *</Label>
              {form.image_url ? (
                <div className="relative mt-2 rounded-xl overflow-hidden border border-slate-200">
                  <img src={form.image_url} alt="" className="w-full h-48 object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, image_url: "" })}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white"
                    data-testid="form-image-remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#1B3A6B] hover:bg-slate-50 transition" data-testid="form-image-upload">
                  {uploading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  ) : (
                    <div className="text-center text-slate-500">
                      <Upload className="w-6 h-6 mx-auto mb-1" />
                      <div className="text-sm">Cliquez pour téléverser</div>
                      <div className="text-xs text-slate-400">JPG / PNG · max 8 Mo</div>
                    </div>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="r-order">Ordre</Label>
                <Input id="r-order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="form-sort-order" />
              </div>
              <div className="flex items-end gap-3 pb-1">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="form-published" />
                <Label className="cursor-pointer">{form.is_published ? "Publié" : "Brouillon"}</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B3A6B] hover:bg-[#142c54]" data-testid="form-save">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer ce chantier ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            "{del?.title}" sera supprimé définitivement. Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)}>Annuler</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
